import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import toast from 'react-hot-toast';
import {
  Eye, EyeOff, Package, User, Lock, Sun, Moon,
  ArrowRight, ShieldCheck, Boxes, TrendingUp, CalendarDays,
} from 'lucide-react';

const DEMO_URL = '/jadwalkan-demo';

const highlights = [
  { icon: Boxes, title: 'Stok real-time', text: 'Kartu stok per gudang, otomatis.' },
  { icon: TrendingUp, title: 'Jurnal otomatis', text: 'Setiap transaksi langsung terposting.' },
  { icon: ShieldCheck, title: 'Peran terbatas', text: 'Akses granular untuk tiap tim.' },
];

export default function LoginPage() {
  const { login } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [form, setForm] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!form.username) newErrors.username = 'Username wajib diisi';
    if (!form.password) newErrors.password = 'Password wajib diisi';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const success = await login(form.username, form.password);
      if (!success) {
        toast.error('Username atau password salah');
      }
    } catch {
      toast.error('Login gagal. Coba lagi nanti.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
      {/* ── Form ── */}
      <div className="flex min-h-screen flex-col px-6 py-8 sm:px-10 lg:px-16 xl:px-24">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-600 shadow-glow">
              <Package size={17} className="text-primary-900" />
            </div>
            <span className="text-[15px] font-semibold tracking-tight text-surface-900 dark:text-white">Stokku</span>
          </div>
          <button
            onClick={toggleTheme}
            className="btn-icon"
            title={isDark ? 'Mode terang' : 'Mode gelap'}
            aria-label={isDark ? 'Mode terang' : 'Mode gelap'}
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>

        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center py-10">
          <div className="animate-rise">
            <span className="chip mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              Portal Internal
            </span>
            <h1 className="text-[2.25rem] leading-[1.15] tracking-[-0.5px] text-surface-900 dark:text-white">
              Masuk ke ruang <span className="text-primary-900 dark:text-primary-400">kerja</span> Anda
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-surface-500 dark:text-surface-400">
              Satu tempat untuk stok, penjualan, pembelian, dan laporan keuangan.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <div>
              <label className="label" htmlFor="username">Username</label>
              <div className="relative">
                <User size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-400" />
                <input
                  id="username"
                  type="text"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  className={`input py-2.5 pl-10 ${errors.username ? 'input-error' : ''}`}
                  placeholder="Masukkan username"
                  autoFocus
                />
              </div>
              {errors.username && <p className="error-text">{errors.username}</p>}
            </div>

            <div>
              <label className="label" htmlFor="password">Password</label>
              <div className="relative">
                <Lock size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-400" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className={`input py-2.5 pl-10 pr-10 ${errors.password ? 'input-error' : ''}`}
                  placeholder="Masukkan password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-surface-400 transition-colors hover:text-surface-700 dark:hover:text-surface-200"
                  aria-label={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && <p className="error-text">{errors.password}</p>}
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full py-2.5">
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Memproses...
                </span>
              ) : (
                <>
                  Masuk
                  <ArrowRight size={15} />
                </>
              )}
            </button>
          </form>

          {/* Jadwalkan demo */}
          <div className="mt-8 rounded-2xl border border-surface-200/80 bg-surface-50/70 p-4 dark:border-surface-800 dark:bg-surface-900/50">
            <div className="mb-3 flex items-center gap-2">
              <CalendarDays size={13} className="text-surface-400" />
              <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-surface-500 dark:text-surface-400">Belum punya akses?</h2>
            </div>
            <p className="mb-3 text-xs leading-relaxed text-surface-500 dark:text-surface-400">
              Akun demo hanya diberikan setelah jadwal disetujui. Ajukan jadwal, lalu kredensial
              dikirim lewat tautan pribadi.
            </p>
            <a
              href={DEMO_URL}
              className="group flex items-center justify-between gap-2 rounded-xl border border-surface-200 bg-white px-3 py-2.5 text-left transition-colors hover:border-primary-400 dark:border-surface-700 dark:bg-surface-900"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-medium text-surface-700 dark:text-surface-200">Jadwalkan demo</span>
                <span className="block text-[10px] text-surface-400">Pilih tanggal dan jam yang kosong</span>
              </span>
              <ArrowRight size={14} className="flex-shrink-0 text-surface-300 transition-transform group-hover:translate-x-0.5 group-hover:text-primary-500" />
            </a>
          </div>

          <p className="mt-6 text-center text-[11px] text-surface-400 dark:text-surface-500">
            Stokku v1.0 &middot; Sistem Akuntansi &amp; Inventory
          </p>
        </div>
      </div>

      {/* ── Visual panel ── */}
      <div className="relative hidden overflow-hidden border-l border-white/60 bg-white/30 backdrop-blur-xl dark:border-white/10 dark:bg-surface-900/40 lg:block">
        <div className="absolute inset-0 bg-grid opacity-70" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600/35 via-transparent to-info-100/60 dark:from-primary-600/10 dark:to-info-500/10" aria-hidden="true" />

        <div className="relative flex h-full flex-col justify-center px-12 py-14 xl:px-16">
          <span className="chip w-fit bg-white/80 backdrop-blur dark:bg-surface-900/80">
            <ShieldCheck size={13} className="text-primary-800 dark:text-primary-400" />
            Lab &middot; Operasi Harian
          </span>

          <h2 className="mt-6 max-w-md text-[2.5rem] leading-[1.15] tracking-[-0.6px] text-surface-900 dark:text-white">
            Stok rapi,
            <br />
            angka <span className="text-primary-900 dark:text-primary-400">selaras</span>.
          </h2>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-surface-500 dark:text-surface-400">
            Setiap barang masuk dan keluar tercatat sekali, lalu mengalir ke jurnal tanpa input ulang.
          </p>

          {/* Floating product card */}
          <div className="relative mt-10 max-w-md">
            <div className="absolute -left-4 -top-4 z-10 animate-rise rounded-full border border-white/80 bg-white/90 px-3 py-1.5 backdrop-blur-xl text-[11px] font-medium text-surface-600 shadow-subtle dark:border-surface-700 dark:bg-surface-900 dark:text-surface-300">
              <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-success-500" />
              Sinkron
            </div>
            <div className="rounded-3xl border border-white/80 bg-white/80 p-5 shadow-floating backdrop-blur-2xl dark:border-white/10 dark:bg-surface-900/80">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-surface-500 dark:text-surface-400">Ringkasan hari ini</p>
                <span className="rounded-full bg-surface-100 px-2 py-0.5 text-[10px] font-medium text-surface-500 dark:bg-surface-800 dark:text-surface-400">Live</span>
              </div>

              <div className="mt-4 flex items-center gap-4 rounded-2xl bg-surface-50 p-4 dark:bg-surface-950/60">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-600 shadow-glow">
                  <Boxes size={22} className="text-primary-900" />
                </div>
                <div>
                  <p className="text-2xl font-medium leading-none tracking-tight text-surface-900 tabular-nums dark:text-white">1.284</p>
                  <p className="mt-1 text-[11px] text-surface-400">SKU aktif di 4 gudang</p>
                </div>
              </div>

              <div className="mt-3 space-y-2">
                {highlights.map((item) => (
                  <div key={item.title} className="flex items-start gap-3 rounded-xl px-1 py-2">
                    <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-900 dark:bg-primary-950 dark:text-primary-300">
                      <item.icon size={13} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium leading-tight text-surface-800 dark:text-surface-100">{item.title}</p>
                      <p className="mt-0.5 text-[11px] leading-tight text-surface-400">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="mt-10 text-[11px] text-surface-400 dark:text-surface-500">
            &copy; {new Date().getFullYear()} Stokku
          </p>
        </div>
      </div>
    </div>
  );
}
